import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef, GridSortDirection } from '@mui/x-data-grid';
import { PaginatedUserFieldsEnumOrderByRecord, usePostApiUserGetPaginatedMutation } from "../../services/klaraApi";
import { Grid, IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import Iconify from "../../components/Iconify";
import { UserCreateEditDialog } from "./UserCreateEditDialog";
import { GridSortItem } from "@mui/x-data-grid/models/gridSortModel";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from 'react-i18next';

export const UserTable = () => {
    const [editActiveId, setEditActiveId] = useState<string | undefined | null>(null);

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortModel, setSortModel] = useState<GridSortItem[]>([
        {
            field: 'email',
            sort: 'asc' as GridSortDirection,
        },
    ]);

    const [fetchPaginatedUsers, { data, isLoading, error }] = usePostApiUserGetPaginatedMutation();

    const updateData = () => {
        fetchPaginatedUsers({
            paginatedUserFieldsEnumPaginatedRequest: {
                search,
                page,
                pageSize,
                orderBy: sortModel as PaginatedUserFieldsEnumOrderByRecord[],
            }
        });
    }

    const { t } = useTranslation();

    useEffect(() => {
        updateData();
    }, [fetchPaginatedUsers, search, page, pageSize, sortModel]);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(t('users.table.userFetchError'), { variant: "error" });
        }
    }, [error, t]);

    const rows = useMemo(() => {
        return data?.data ?? [];
    }, [data]);

    const columns: GridColDef[] = useMemo(() => [
        {
            field: 'email',
            headerName: t('users.table.email').toString(),
            width: 200,
        },
        {
            field: 'username',
            headerName: t('users.table.username').toString(),
            width: 150,
        },
        {
            field: 'firstName',
            headerName: t('users.table.firstName').toString(),
            width: 150,
        },
        {
            field: 'lastName',
            headerName: t('users.table.lastName').toString(),
            width: 150,
        },
        {
            field: 'joinedAt',
            headerName: t('users.table.joined').toString(),
            type: 'number',
            width: 150,
        },
        {
            field: 'actions',
            renderHeader: () => (
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => setEditActiveId(undefined)}>
                        <Iconify icon={"mdi:plus"} />
                    </IconButton>
                </Stack>
            ),
            renderCell: (params) => (
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => setEditActiveId(params.id as string)}>
                        <Iconify icon={"mdi:pencil"} />
                    </IconButton>
                </Stack>
            ),
            flex: 1,
            align: "right",
            headerAlign: "right",
            sortable: false,
        }
    ], [t]);


    return (
        <>
            <Stack spacing={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            autoFocus
                            fullWidth
                            label={t('users.table.search')}
                            variant="outlined"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end">
                                        <Iconify icon={"mdi:magnify"} />
                                    </InputAdornment>,
                            }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ height: 520, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        rowCount={data?.total ?? 0}
                        paginationMode="server"
                        pageSizeOptions={[10, 25, 50, 100]}
                        onPaginationModelChange={(params) => {
                            setPage(params.page);
                            setPageSize(params.pageSize);
                        }}
                        paginationModel={{
                            page,
                            pageSize,
                        }}
                        sortModel={sortModel}
                        onSortModelChange={(params) => {
                            setSortModel(params);
                        }}
                        loading={isLoading}
                        columns={columns}
                    />
                </Box>
            </Stack>

            <UserCreateEditDialog
                open={editActiveId !== null}
                existingUserId={editActiveId}
                onClose={() => {
                    setEditActiveId(null);
                    updateData();
                }}
            />
        </>
    );
}
